import React from 'react';
import styled, { css } from 'styled-components';

import { Chip, Icon, formatNumber } from '@column/column-ui-kit';

import { CopyRow } from '~/app/pages/Transfers/_components/CopyRow';
import { Avatar } from '~/app/pages/Transfers/_components/Timeline/Avatar';
import { TimelineStatus } from '~/app/pages/Transfers/_components/Timeline/types';
import { Card, Flex, Inner, Text } from '~/styles';

export interface TransferPartyDetail {
  label: string;
  value: string | React.ReactNode;
  warning?: string;
  isMonospaced?: boolean;
}

export interface TransferPartyAmount {
  value: number;
  currencyCode?: string;
  isAdding?: boolean;
  partyType?: 'sender' | 'receiver';
  isIncoming?: boolean;
}

export interface TransferPartyProps {
  icon?: React.ReactNode;
  title: string;
  secondaryTitle?: string;
  subtitle?: string | React.ReactNode;
  amount?: TransferPartyAmount;
  details?: TransferPartyDetail[];
  metadata?: string;
  onClick?: () => void;
  status?: TimelineStatus;
  variant?: 'primary' | 'secondary';
  showCurrencyIcon?: boolean;
}

const Title = styled.span`
  display: flex;
  align-items: center;
  line-height: 16px;
  max-height: 16px;

  svg {
    opacity: 0;
    transform: translateX(-4px);
    transition:
      opacity 0.2s,
      transform 0.2s;
  }
`;

const Subtitle = styled(Text)<{ status?: TimelineStatus }>`
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    --icon-size: 16px;
  }

  span {
    color: ${({ theme }) => theme.secondary.blendToBackground(500)};
  }

  ${({ status }) =>
    status === 'pending' &&
    css`
      background: linear-gradient(
        to right,
        ${({ theme }) => theme.secondary.blendToBackground(100)} 20%,
        ${({ theme }) => theme.secondary.blendToBackground(400)} 40%,
        ${({ theme }) => theme.secondary.blendToBackground(400)} 60%,
        ${({ theme }) => theme.secondary.blendToBackground(100)} 80%
      );
      background-size: 200% auto;

      color: ${({ theme }) => theme.secondary.blendToBackground(800)};
      background-clip: text;
      text-fill-color: transparent;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;

      animation: pending 2s linear infinite;
      @keyframes pending {
        to {
          background-position: -200% center;
        }
      }
    `}

  ${({ status }) => status === 'success' && css``}
  ${({ status }) =>
    status === 'danger' &&
    css`
      color: ${({ theme }) => theme.danger.background};
      text-decoration: line-through;

      span {
        color: ${({ theme }) => theme.danger.blendToBackground(500)};
      }
    `}
`;

const Header = styled.div<{ onClick?: () => void }>`
  display: flex;
  align-items: start;
  padding: 8px;
  border-radius: 5px;
  transition:
    background 0.2s,
    box-shadow 0.2s;

  ${({ onClick }) =>
    onClick &&
    css`
      &:hover {
        cursor: pointer;
        background: ${({ theme }) => theme.secondary.blendToBackground(25)};
        box-shadow: 0 0 0 1px ${({ theme }) => theme.secondary.blendToBackground(150)};

        svg {
          opacity: 1;
          transform: translateX(0);
        }
      }

      &:active {
        ${Title} svg {
          transform: translateX(2px);
          transition: transform 0.1s;
        }
      }
    `}
`;

const StyledCard = styled(Card)`
  min-width: 248px;
  margin-bottom: 16px;
  margin-left: 28px;

  @media (min-width: ${({ theme }) => theme.breakpoints.desktop}) {
    margin-left: 0;
  }
`;

const StyledChip = styled(Chip)`
  margin-top: -4px;
`;

const formatAmountSubtitle = (amount?: TransferPartyAmount): React.ReactNode | null => {
  if (!amount) return null;

  const currencyCode = amount.currencyCode ?? 'USD';

  return (
    <>
      {`${amount.isAdding === false ? '-' : ''}${formatNumber(amount.value)}`} <span>{currencyCode}</span>
    </>
  );
};

const getCurrencyIcon = (amount?: TransferPartyAmount): React.ReactNode => {
  if (!amount || !amount.currencyCode) return null;

  const CurrencyIcon = Icon.Currency[amount.currencyCode as keyof typeof Icon.Currency]
    ? Icon.Currency[amount.currencyCode as keyof typeof Icon.Currency]
    : Icon.Currency.USD;
  return <CurrencyIcon />;
};

export const TransferParty: React.FC<TransferPartyProps> = ({
  icon,
  title,
  secondaryTitle,
  subtitle,
  amount,
  details,
  metadata,
  onClick,
  status,
  variant,
  showCurrencyIcon,
}) => {
  const amountSubtitle = subtitle !== undefined ? subtitle : formatAmountSubtitle(amount);
  const currencyIcon = showCurrencyIcon ? getCurrencyIcon(amount) : null;

  return (
    <StyledCard variant={variant}>
      <Inner p={8}>
        <Header onClick={onClick}>
          <Flex gap={12} alignItems="center" auto>
            {icon && <Avatar size="large">{icon}</Avatar>}
            <Flex gap={4} flexDirection="column">
              <Title>
                <Flex gap={4} alignItems="center">
                  <Text as="h4" size={14} weight="medium">
                    {title}
                  </Text>
                  {secondaryTitle && (
                    <Text as="h4" color="light" size={14} weight="medium">
                      {secondaryTitle}
                    </Text>
                  )}
                  {onClick && <Icon.ChevronRight />}
                </Flex>
              </Title>
              {amountSubtitle && (
                <Subtitle size={16} weight="medium" status={status} color="light">
                  {currencyIcon} {amountSubtitle}
                </Subtitle>
              )}
            </Flex>
          </Flex>
          {metadata && <StyledChip size="large">{metadata}</StyledChip>}
        </Header>
      </Inner>
      {details &&
        details.map(({ label, value, warning, isMonospaced }) => (
          <CopyRow key={label} label={label} value={value} warning={warning} isMonospaced={isMonospaced} />
        ))}
    </StyledCard>
  );
};
